import { AllCoursesQueryVariables, GroupEdge, useAllCoursesQuery, PageInfo } from 'core/graphql';
import { ICourseCard } from 'components/molecules';

import { useCallback } from 'react';
import { transformCoursesNode } from './transform';

export type CoursesFilterVariables = Pick<
  AllCoursesQueryVariables,
  'active' | 'finished' | 'search' | 'courseId' | 'groupIds' | 'lessonDate'
>;

type coursesType = {
  data: ICourseCard[];
  loading?: boolean;
  error?: string;
  totalCount?: number | null;
  pageInfo?: Pick<PageInfo, 'startCursor' | 'endCursor' | 'hasNextPage' | 'hasPreviousPage'>;
  operations: {
    fetchMoreData: (after?: AllCoursesQueryVariables['after']) => void;
  };
};

const useGetCoursesFromApi = (filters: CoursesFilterVariables) => {
  // Filter changes flow through variables: Apollo refetches and, thanks to the
  // relayStylePagination keyArgs in the cache, swaps to the list of that filter set.
  const { data, loading, error, fetchMore } = useAllCoursesQuery({
    fetchPolicy: 'network-only',
    variables: {
      first: 5,
      ...filters,
    },
    notifyOnNetworkStatusChange: true,
  });

  const result = useCallback((): ICourseCard[] => {
    const edges = data?.allGroupsForLms?.edges;
    if (!edges) {
      return [];
    }

    return edges.reduce((acc: ICourseCard[], course) => {
      if (course) {
        const coursesArray = transformCoursesNode(course as GroupEdge);
        if (coursesArray) {
          return [...acc, coursesArray];
        }
      }
      return acc;
    }, []);
  }, [data?.allGroupsForLms?.edges]);

  return {
    fetchMore,
    result: result(),
    error,
    loading,
    pageInfo: data?.allGroupsForLms?.pageInfo,
    totalCount: data?.allGroupsForLms?.totalCount,
  };
};

export const useCourses = (filters: CoursesFilterVariables = {}): coursesType => {
  const {
    result,
    error,
    loading,
    totalCount,
    pageInfo,
    fetchMore = () => null,
  } = useGetCoursesFromApi(filters);

  // Only for after-cursor pagination — the current filter vars are forwarded as-is.
  const fetchMoreData = useCallback(
    (after?: AllCoursesQueryVariables['after']) => {
      fetchMore({
        variables: {
          ...filters,
          first: 5,
          after,
        },
      });
    },
    [fetchMore, filters],
  );

  return {
    data: result || [],
    loading,
    error: error?.message || '',
    totalCount,
    pageInfo,
    operations: {
      fetchMoreData,
    },
  };
};
